import { useState } from 'react';
import {
  PencilIcon,
  TrashIcon,
  DocumentDuplicateIcon,
  CheckBadgeIcon,
  CalendarIcon,
} from '@heroicons/react/24/outline';
import { CheckBadgeIcon as CheckBadgeIconSolid } from '@heroicons/react/24/solid';

interface MappingCardProps {
  mapping: {
    id: string;
    name: string;
    source_system: string;
    is_default: boolean;
    field_count: number;
    created_at: string;
    updated_at: string;
  };
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  onDuplicate: (id: string) => void;
  onSetDefault: (id: string) => void;
}

const sourceSystemColors: Record<string, string> = {
  QUICKBOOKS: 'bg-green-100 text-green-800',
  ADP: 'bg-blue-100 text-blue-800',
  PAYCHEX: 'bg-purple-100 text-purple-800',
  GUSTO: 'bg-orange-100 text-orange-800',
  WORKDAY: 'bg-pink-100 text-pink-800',
};

export default function MappingCard({
  mapping,
  onEdit,
  onDelete,
  onDuplicate,
  onSetDefault,
}: MappingCardProps) {
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = () => {
    if (window.confirm(`Are you sure you want to delete "${mapping.name}"?`)) {
      setIsDeleting(true);
      onDelete(mapping.id);
    }
  };

  const handleSetDefault = () => {
    if (!mapping.is_default) {
      onSetDefault(mapping.id);
    }
  };

  const formattedDate = new Date(mapping.updated_at).toLocaleDateString(
    'en-US',
    {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }
  );

  return (
    <div
      className={`bg-white rounded-lg shadow hover:shadow-md transition-shadow duration-200 ${
        isDeleting ? 'opacity-50 pointer-events-none' : ''
      }`}
    >
      <div className="p-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold text-gray-900 truncate">
                {mapping.name}
              </h3>
              {mapping.is_default && (
                <CheckBadgeIconSolid
                  className="h-5 w-5 text-green-600 flex-shrink-0"
                  title="Default mapping"
                />
              )}
            </div>
            <div className="mt-2 flex items-center gap-2">
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  sourceSystemColors[mapping.source_system] ||
                  'bg-gray-100 text-gray-800'
                }`}
              >
                {mapping.source_system}
              </span>
              <span className="text-sm text-gray-500">
                {mapping.field_count}{' '}
                {mapping.field_count === 1 ? 'field' : 'fields'}
              </span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-4 flex items-center justify-between">
          <div className="flex items-center text-xs text-gray-500">
            <CalendarIcon className="h-4 w-4 mr-1" />
            Updated {formattedDate}
          </div>

          <div className="flex items-center gap-1">
            {/* Edit Button */}
            <button
              onClick={() => onEdit(mapping.id)}
              className="p-2 text-gray-400 hover:text-blue-600 transition-colors rounded-lg hover:bg-blue-50"
              title="Edit mapping"
            >
              <PencilIcon className="h-5 w-5" />
            </button>

            {/* Set as Default Button */}
            {!mapping.is_default && (
              <button
                onClick={handleSetDefault}
                className="p-2 text-gray-400 hover:text-green-600 transition-colors rounded-lg hover:bg-green-50"
                title="Set as default"
              >
                <CheckBadgeIcon className="h-5 w-5" />
              </button>
            )}

            {/* Duplicate Button */}
            <button
              onClick={() => onDuplicate(mapping.id)}
              className="p-2 text-gray-400 hover:text-purple-600 transition-colors rounded-lg hover:bg-purple-50"
              title="Duplicate mapping"
            >
              <DocumentDuplicateIcon className="h-5 w-5" />
            </button>

            {/* Delete Button */}
            <button
              onClick={handleDelete}
              className="p-2 text-gray-400 hover:text-red-600 transition-colors rounded-lg hover:bg-red-50"
              title="Delete mapping"
            >
              <TrashIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
